import * as React from 'react';

import {Button} from '../../button/button';
import {RemoveButton} from '../../button/remove-button/remove-button';
import {StackLegacy} from '../../layout/stack/stack-legacy';
import {TextField} from '../../textfield/textfield';
import type {TextFieldProps} from '../../textfield/textfield';
import {TextFieldDebounced} from '../../textfield/textfield-debounced';

import {ErrorMessage} from '../error-message';

export type ItemConfig<T> = {
    getValue: (item: T) => string;
    setValue: (item: T, value: string) => T;
    createNew: () => T;
    getKey: (item: T, index: number) => string;
};

type Props<T> = {
    items: T[];
    onChange: (items: T[]) => void;
    itemConfig: ItemConfig<T>;
    placeholder?: string;
    addButtonText?: string;
    maxItems?: number;
    minItems?: number;
    textFieldProps?: Partial<TextFieldProps>;
    isDisabled?: boolean;
    hasError?: boolean;
    errorMessage?: string;
    focusOnMount?: boolean;
    // Debounce support (mirrors CharacterLimitedTextfield behavior)
    isDebouncedSave?: boolean;
    debouncedSaveDelay?: number;
};

/**
 * A reusable component for managing multiple text inputs with add/remove functionality.
 * Similar to the pattern used in invite-users but made into a reusable shell component.
 */
export function MultiAddTextField<T>(props: Props<T>) {
    const {
        items,
        onChange,
        itemConfig,
        placeholder = 'Enter value...',
        addButtonText = 'Add another',
        maxItems,
        minItems = 1,
        textFieldProps = {},
        isDisabled = false,
        hasError = false,
        errorMessage,
        focusOnMount = false,
        isDebouncedSave = true,
        debouncedSaveDelay,
    } = props;

    const handleItemChange = React.useCallback(
        (index: number, newValue: string) => {
            const newItems = items.map((item, i) => {
                if (i !== index) {
                    return item;
                }

                return itemConfig.setValue(item, newValue);
            });

            onChange(newItems);
        },
        [items, onChange, itemConfig]
    );

    const handleAddItem = React.useCallback(() => {
        if (maxItems && items.length >= maxItems) {
            return;
        }

        // Do not allow adding if any existing item is empty
        const hasEmptyExistingItem = items.some(
            (existingItem) => itemConfig.getValue(existingItem).trim() === ''
        );
        if (hasEmptyExistingItem) {
            return;
        }

        const newItem = itemConfig.createNew();
        onChange([...items, newItem]);
    }, [items, onChange, maxItems, itemConfig]);

    const handleRemoveItem = React.useCallback(
        (index: number) => {
            if (items.length <= minItems) {
                return;
            }

            const newItems = items.filter((_, i) => i !== index);
            onChange(newItems);
        },
        [items, onChange, minItems]
    );

    const hasEmptyItems = items.some((it) => itemConfig.getValue(it).trim() === '');
    const canAddMore = (!maxItems || items.length < maxItems) && !hasEmptyItems;
    const canRemove = items.length > minItems;

    return (
        <StackLegacy spacing={8} isFullWidth={true}>
            {hasError && errorMessage && <ErrorMessage size='small'>{errorMessage}</ErrorMessage>}
            {items.map((item, index) => {
                const commonProps = {
                    name: `item-${index}`,
                    placeholder,
                    value: itemConfig.getValue(item),
                    // @ts-expect-error
                    onChange: (value) => handleItemChange(index, value),
                    disabled: isDisabled,
                    hasError,
                    focusOnMount: focusOnMount && index === items.length - 1,
                    selectOnFocus: true,
                    ...textFieldProps,
                } as const;

                return (
                    <div key={itemConfig.getKey(item, index)} className='flex align-center gap-8'>
                        {isDebouncedSave ? (
                            // @ts-expect-error
                            <TextFieldDebounced
                                {...commonProps}
                                delay={debouncedSaveDelay != null ? debouncedSaveDelay : 800}
                            />
                        ) : (
                            <TextField {...commonProps} />
                        )}
                        {canRemove && (
                            <RemoveButton
                                onClick={() => handleRemoveItem(index)}
                                // @ts-expect-error
                                disabled={isDisabled}
                            />
                        )}
                    </div>
                );
            })}
            {canAddMore && (
                <Button
                    onClick={handleAddItem}
                    variant='text-add'
                    noPadding={true}
                    disabled={isDisabled}
                >
                    {addButtonText}
                </Button>
            )}
        </StackLegacy>
    );
}
